import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePreferencesStore } from '@/state/preferences';
import { patchPreference } from '@/lib/preferencesApi';

const DEBOUNCE_MS = 750;

// One timer + one pending-payload slot per path, shared across all hook instances
// so multiple components reading the same path share a debounce window.
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const pending = new Map<string, unknown>();

function readAtPath<T>(tree: unknown, path: string, fallback: T): T {
  const segments = path.split('.');
  let cursor: unknown = tree;
  for (const segment of segments) {
    if (typeof cursor !== 'object' || cursor === null) return fallback;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor === undefined ? fallback : (cursor as T);
}

export function usePreference<T>(
  path: string,
  defaultValue: T
): [T, (next: T) => void] {
  const value = usePreferencesStore(
    (state) => readAtPath(state.preferences, path, defaultValue) as T
  );

  const defaultRef = useRef(defaultValue);
  useEffect(() => {
    defaultRef.current = defaultValue;
  }, [defaultValue]);

  const setValue = useCallback(
    (next: T) => {
      const prev = readAtPath(
        usePreferencesStore.getState().preferences,
        path,
        defaultRef.current
      );
      usePreferencesStore.getState().setPath(path, next);
      pending.set(path, next);

      const existing = timers.get(path);
      if (existing) clearTimeout(existing);

      const timer = setTimeout(() => {
        const toSend = pending.get(path);
        pending.delete(path);
        timers.delete(path);
        patchPreference(path, toSend)
          .then((tree) => {
            // Replace local store with server truth AND broadcast to other
            // tabs via setAll's BroadcastChannel. The server-truth value
            // should match the optimistic update (or be more correct), so
            // overwriting the optimistic path update is acceptable.
            usePreferencesStore.getState().setAll(tree);
          })
          .catch(() => {
            usePreferencesStore.getState().setPath(path, prev);
          });
      }, DEBOUNCE_MS);

      timers.set(path, timer);
    },
    [path]
  );

  return useMemo(() => [value, setValue], [value, setValue]);
}
